// src/components/performance/form/sections/ContactInformationSection.tsx

import React from 'react';
import {
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  SelectChangeEvent
} from '@mui/material';

interface ContactInformationSectionProps {
  formData: {
    primaryContact: string;
    contactEmail: string;
    contactPhone: string;
    genre: string;
    ensembleMakeup: 'all-male' | 'all-female' | 'mixed' | 'predominantly-male' | 'predominantly-female' | 'female-led' | 'other';
    bookingStatus: 'PROVISIONAL' | 'CHECKING' | 'BOOKED' | 'AVAILABLE';
    bookingNotes: string;
  };
  onFieldChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<string>) => void;
  isEdit?: boolean;
  showSection?: boolean; // Control when to show this section
}

const JAZZ_GENRES = [
  'Traditional Jazz',
  'Bebop',
  'Cool Jazz',
  'Hard Bop',
  'Modal Jazz',
  'Free Jazz',
  'Fusion', 
  'Contemporary Jazz',
  'Smooth Jazz',
  'Latin Jazz',
  'Jazz Funk',
  'Acid Jazz',
  'Nu Jazz',
  'Swing',
  'Dixieland',
  'Post-Bop',
  'Avant-garde Jazz',
  'Jazz Rock',
  'Other'
];

export const ContactInformationSection: React.FC<ContactInformationSectionProps> = ({
  formData,
  onFieldChange,
  isEdit = false,
  showSection = true
}) => {
  // Don't render if we shouldn't show this section
  if (!showSection) return null;

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h6" color="primary">
          Contact Information
          {!isEdit && (
            <Typography variant="caption" display="block" color="text.secondary">
              Required for contracting and communication
            </Typography>
          )}
        </Typography>
      </Grid>

      {/* Primary Contact */}
      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          name="primaryContact"
          label="Primary Contact Name"
          value={formData.primaryContact}
          onChange={onFieldChange}
          required
          helperText="Main contact person for this performance"
        />
      </Grid>

      {/* Contact Email */}
      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          name="contactEmail"
          label="Contact Email"
          type="email"
          value={formData.contactEmail}
          onChange={onFieldChange}
          required
          helperText="Email for contracts and communication"
        />
      </Grid>

      {/* Contact Phone */}
      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          name="contactPhone"
          label="Contact Phone"
          value={formData.contactPhone}
          onChange={onFieldChange}
          required
          helperText="Phone number for urgent contact"
        />
      </Grid>

      {/* Genre */}
      <Grid item xs={12} md={6}>
        <FormControl fullWidth>
          <InputLabel>Genre</InputLabel>
          <Select
            name="genre"
            value={formData.genre}
            onChange={onFieldChange}
            label="Genre"
          >
            <MenuItem value="">
              <em>Not specified</em>
            </MenuItem>
            {JAZZ_GENRES.map((genre) => (
              <MenuItem key={genre} value={genre}>
                {genre}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      {/* Ensemble Makeup */}
      <Grid item xs={12} md={6}>
        <FormControl fullWidth>
          <InputLabel>Ensemble Makeup</InputLabel>
          <Select
            name="ensembleMakeup"
            value={formData.ensembleMakeup}
            onChange={onFieldChange}
            label="Ensemble Makeup"
          >
            <MenuItem value="mixed">Mixed Ensemble</MenuItem>
            <MenuItem value="all-male">All Male</MenuItem>
            <MenuItem value="all-female">All Female</MenuItem>
            <MenuItem value="female-led">Female Led</MenuItem>
            <MenuItem value="predominantly-male">Predominantly Male</MenuItem>
            <MenuItem value="predominantly-female">Predominantly Female</MenuItem>
            <MenuItem value="other">Other/Non-binary</MenuItem>
          </Select>
        </FormControl>
      </Grid>

      {/* Booking Status */}
      <Grid item xs={12} md={6}>
        <FormControl fullWidth>
          <InputLabel>Booking Status</InputLabel>
          <Select
            name="bookingStatus"
            value={formData.bookingStatus}
            onChange={onFieldChange}
            label="Booking Status"
          >
            <MenuItem value="PROVISIONAL">Provisional</MenuItem>
            <MenuItem value="CHECKING">Checking</MenuItem>
            <MenuItem value="BOOKED">Booked</MenuItem>
            <MenuItem value="AVAILABLE">Available</MenuItem>
          </Select>
        </FormControl>
      </Grid>

      {/* Booking Notes */}
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          name="bookingNotes"
          label="Booking Notes"
          value={formData.bookingNotes}
          onChange={onFieldChange}
          multiline
          rows={2}
          helperText="Quick notes about this booking"
        />
      </Grid>
    </Grid>
  );
};

export default ContactInformationSection;